import * as React from "react";

import { AspectRatio, Box, Heading, Text } from "@vinomofo/components";
import { sampleSize, toLower } from "lodash";

import { ArticleTileProps } from "./ArticleTile";
import { GatsbyImage } from "gatsby-plugin-image";
import { Link } from "../../link";
import styled from "styled-components";
import { trackEvent } from "../../../utils/track-event";

const Container = styled(Box)`
  transition: color 0.3s ease-in-out;

  p {
    font-family: DM Sans, sans-serif;
  }
`;

const TileLink = styled(Link)<{ feature: boolean }>`
  text-decoration: none;
  color: inherit;
  // text-decoration: ${({ feature }) => (!feature ? "underline" : "none")};
  font-family: inherit;

  :hover {
    text-decoration: underline;
  }

  ::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
`;

const Tags = styled(Box)`
  color: "white";

  .divider {
    width: 1px;
    background-color: #f3b659;
  }
`;

export const WhiskyArticleTile = ({
  id,
  title,
  image,
  link,
  bodyText,
  isLoading = false,
  isFeatured = false,
  tags,
}: ArticleTileProps): JSX.Element => {
  const safeTags = tags ?? [];
  const [firstTag, ...otherTags] = safeTags;
  const footerTags = isFeatured ? safeTags : otherTags;

  return (
    <Container
      key={id}
      display="flex"
      gridGap="8px"
      width="100%"
      overflow="hidden"
      flexDirection={
        isFeatured ? ["column", "row-reverse", "row-reverse"] : "unset"
      }
      color="white"
      fontFamily="DM Sans, sans-serif"
      position="relative"
    >
      <Box
        overflow="hidden"
        maxWidth={isFeatured ? ["100%", "40%", "40%"] : "30%"}
        mb={isFeatured ? ["12px", 0, 0] : 0}
      >
        {isLoading ? (
          <AspectRatio bg="white" />
        ) : (
          <GatsbyImage
            image={image}
            style={{
              width: "100%",
              height: "100%",
            }}
            alt=""
          />
        )}
      </Box>
      <Box color="white">
        {!isFeatured && firstTag && (
          <Box mb={["10px", "15px"]}>
            <Text variant="md" letterSpacing={0.3} fontSize={1} color="white">
              {firstTag}
            </Text>
          </Box>
        )}
        {isLoading ? (
          <Box bg="white" height={["16px", "20px"]} />
        ) : (
          <Heading
            variant={isFeatured ? "lg" : "xxs"}
            color="inherit"
            lineHeight={1.2}
            fontFamily="DM Sans, sans-serif"
            fontWeight="400"
            letterSpacing="1px"
          >
            <TileLink
              to={link}
              isExternal
              feature={isFeatured}
              onClick={() => {
                trackEvent("Article Clicked", {
                  id: id,
                  title: title,
                  slug: link,
                  tags: safeTags,
                  featured: isFeatured,
                  type: "Whiskymofo Article",
                });
              }}
            >
              {title}
            </TileLink>
          </Heading>
        )}
        {isFeatured && bodyText && (
          <Box mt={["10px", "15px"]}>
            {isLoading ? (
              <Box bg="white" height={["210px", "144px"]} />
            ) : (
              <Text variant="md" letterSpacing={0.3} fontSize={3} color="white">
                {bodyText}
              </Text>
            )}
          </Box>
        )}
        {footerTags && (
          <Tags mt={["10px", "15px"]} gridGap="14px" display="flex">
            {footerTags.map((tag, i) => {
              const isLast = footerTags.length == i + 1;
              const characterPool =
                "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789" + i + tag;
              const randomString = sampleSize(characterPool, 10).join("");
              const randomText = toLower(randomString);

              return (
                <React.Fragment key={randomText}>
                  <Text
                    key={randomText}
                    variant="md"
                    letterSpacing={0.3}
                    fontSize={isFeatured ? 2 : 1}
                    color="white"
                  >
                    {tag}
                  </Text>
                  {!isLast && (
                    <Text
                      key={randomText + "spacer"}
                      variant="md"
                      letterSpacing={0.3}
                      fontSize={isFeatured ? 2 : 1}
                      color="transparent"
                      className="divider"
                    >
                      &nbsp;
                    </Text>
                  )}
                </React.Fragment>
              );
            })}
          </Tags>
        )}
      </Box>
    </Container>
  );
};
